import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from './cn';
import { Button } from './Button';
import { Alert } from './States';
import { InfoTip } from './InfoTip';

/**
 * A modal, and the confirmation built on it.
 *
 * Closes on Escape and on a backdrop click, never on a click inside it: a
 * dialog that dismisses itself while somebody is halfway through typing an
 * approval loses the approval, and the second attempt is the one people get
 * wrong.
 *
 * Focus is trapped and returned. Without the trap, tabbing out of a modal
 * lands on the page behind it — which for a screen-reader user means the
 * dialog has effectively vanished while still covering everything.
 */

function useDialogBehaviour(onClose: () => void, active = true) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  // Held in a ref so the effect below runs once per open, not once per
  // render. Callers pass `onClose={() => setX(null)}` — a new function every
  // render — and re-running the effect on each keystroke re-focused the
  // dialog's first control: the close button. The next space bar pressed it.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!active) return undefined;
    restoreTo.current = document.activeElement as HTMLElement | null;

    // The page behind must not scroll under the overlay — on a long list, the
    // reader closes the dialog and finds themselves somewhere else entirely.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the first thing worth focusing, not the container: an autofocused
    // reason field is the difference between typing and hunting for it.
    // The first control in the body or the footer, not the ✕ in the title
    // bar: landing on Close means the first Enter dismisses a half-typed form,
    // and a screen reader announces "Close" before it announces the question.
    // Nor an ⓘ: focus opens it, and the dialog would open on a tooltip.
    const focusable = Array.from(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    (focusable.find((el) => !el.hasAttribute('data-dialog-close') && !el.hasAttribute('data-infotip'))
      ?? focusable[0] ?? ref.current)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // An open picker inside the dialog owns the first Escape: it closes
        // the list. Closing the whole dialog underneath somebody who was only
        // dismissing a dropdown loses the form they were filling in.
        const target = e.target as HTMLElement | null;
        if (target?.closest?.('[aria-expanded="true"]')) return;
        e.stopPropagation();
        close.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = Array.from(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
        .filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;

      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      restoreTo.current?.focus?.();
    };
  }, [active]);

  return ref;
}

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export function Dialog({
  title, info, children, onClose, wide, describedBy, open = true, footer, document: doc,
}: {
  title: string;
  /** What the dialog is for, as an ⓘ beside the title rather than a paragraph under it. */
  info?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  describedBy?: string;
  /**
   * Whether the dialog is mounted at all.
   *
   * Optional and defaulting to true, because most callers here render the
   * component conditionally and never pass it. It exists for the callers that
   * would rather keep the element in the tree and toggle a flag — both are
   * reasonable, and a primitive that only supports one of them pushes the
   * other into a wrapper.
   */
  open?: boolean;
  /**
   * The action row, pinned below the content.
   *
   * Passing actions here rather than as the last child means they sit outside
   * the scrolling region: on a long form the buttons stay reachable instead of
   * being somewhere below the fold of a nested scroll.
   */
  footer?: ReactNode;
  /**
   * A document viewer (a PDF in a frame): as large as the screen allows,
   * and the whole screen on a phone, where a framed page is unreadable.
   */
  document?: boolean;
}) {
  const titleId = useId();
  const ref = useDialogBehaviour(onClose, open);

  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-40 flex items-start justify-center bg-neutral-950/40 animate-fade-in sm:items-center',
        doc ? 'p-0 sm:p-6' : 'p-4 sm:p-8',
      )}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* The dialog scrolls inside itself, not the overlay: on a long form the
          title and the actions stay where they are and only the content moves. */}
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={cn(
          'flex max-h-full w-full flex-col rounded-modal bg-white shadow-modal outline-none animate-slide-up',
          doc ? 'h-full max-w-5xl max-sm:rounded-none sm:h-[90vh]' : wide ? 'max-w-2xl' : 'max-w-md',
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 px-5 pt-5">
          <div className="flex items-center gap-1">
            <h2 id={titleId} className="text-h4 text-neutral-900">{title}</h2>
            {info && <InfoTip label={`About ${title}`}>{info}</InfoTip>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-dialog-close=""
            className="-mr-2 -mt-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-btn text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5">
          {children}
        </div>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A right-hand panel, for detail that should not lose the list behind it.
 *
 * Same behaviour as the modal — trapped, escapable, scroll-locked — because
 * from the keyboard's point of view it is one.
 */
export function Drawer({ title, children, onClose, width = 'md' }: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  width?: 'md' | 'lg' | 'xl';
}) {
  const titleId = useId();
  const ref = useDialogBehaviour(onClose);
  const WIDTH = { md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl' } as const;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-neutral-950/40 animate-fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'flex h-full w-full flex-col bg-white shadow-modal outline-none',
          WIDTH[width],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <h2 id={titleId} className="text-h4 text-neutral-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-dialog-close=""
            className="-mr-2 -my-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-btn text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  title: string;
  /** What is about to happen, in a sentence. Not "Are you sure?". */
  what: ReactNode;
  /** Who or what it lands on — the count, the names, the company. */
  affects?: ReactNode;
  /** Anything that cannot be undone afterwards. Rendered as a warning. */
  irreversible?: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger' | 'success';
  /**
   * Demands a written reason and hands it to `onConfirm`.
   *
   * On this platform a reason is not a formality: for anything cross-tenant
   * the API refuses without one, and what gets typed lands in an audit row
   * the affected company can later ask to see. So the field is required
   * rather than optional-with-a-nudge, and the confirm button stays disabled
   * until it says something.
   */
  requireReason?: boolean;
  reasonLabel?: string;
  reasonHint?: string;
  /**
   * The shortest reason the API accepts. It differs by act — five characters
   * for a lost lead, ten for re-routing a case — and a button that enables
   * at three only to be refused by the server leaves the operator guessing.
   */
  minReason?: number;
  onConfirm: (reason: string) => Promise<void> | void;
  onCancel: () => void;
  /** Extra controls between the statement and the reason — a coded reason, a date. */
  children?: ReactNode;
}

/**
 * The confirmation for an act that is hard to take back.
 *
 * Deliberately not "Are you sure?" — that question carries no information and
 * is answered yes by reflex. This one states what will happen, who it lands
 * on, and what cannot be undone, and for elevated acts it asks why in the
 * operator's own words.
 */
export function ConfirmDialog({
  title, what, affects, irreversible, confirmLabel, tone = 'primary',
  requireReason, reasonLabel = 'Reason', reasonHint, minReason = 3,
  onConfirm, onCancel, children,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyId = useId();

  const blocked = requireReason && reason.trim().length < minReason;

  const confirm = useCallback(async () => {
    if (blocked) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      // Stay open on failure. Closing over an error means the operator
      // believes the thing happened.
      setError(err instanceof Error ? err.message : 'That did not go through.');
      setBusy(false);
    }
  }, [blocked, onConfirm, reason]);

  return (
    <Dialog title={title} onClose={busy ? () => undefined : onCancel} describedBy={bodyId}>
      <div id={bodyId} className="mt-3 space-y-3">
        <p className="text-body text-neutral-700">{what}</p>

        {affects && (
          <div className="rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small text-neutral-700">
            {affects}
          </div>
        )}

        {irreversible && (
          <Alert tone="warning">
            <AlertTriangle className="mr-1.5 inline h-4 w-4 align-text-bottom" />
            {irreversible}
          </Alert>
        )}

        {children}
        {requireReason && (
          <label className="block">
            <span className="text-caption text-neutral-700">{reasonLabel} <span className="text-neutral-500">(required)</span></span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              aria-describedby={`${bodyId}-reason`}
              className="mt-1 w-full rounded-input border border-neutral-300 bg-white px-3 py-2 text-body text-neutral-900
                         focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {/* Says why the button is still disabled, rather than leaving a
                greyed confirm for the operator to puzzle over. */}
            <span id={`${bodyId}-reason`} className="mt-1 block text-caption font-normal text-neutral-500">
              {reasonHint ?? 'Recorded in the audit trail against your name.'}
              {blocked && ` Write at least ${minReason} characters to enable the button.`}
            </span>
          </label>
        )}

        {error && <Alert tone="danger">{error}</Alert>}
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant={tone} onClick={confirm} loading={busy} disabled={blocked}>
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
